import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../api';

const pad = n => String(n).padStart(2,'0');
const toISO = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const fmtDate = s => { const d=new Date(s+'T00:00:00'); return `${pad(d.getDate())}.${pad(d.getMonth()+1)}`; };

const METRICS = [
  { key:'orders_sum',   label:'Сумма заказов',  color:'#10b981', yAxis:'money', fmt:v=>Math.round(v).toLocaleString('ru-RU')+' ₽' },
  { key:'spend_all',    label:'Расходы рекл.',   color:'#ef4444', yAxis:'money', fmt:v=>Math.round(v).toLocaleString('ru-RU')+' ₽' },
  { key:'client_price', label:'Цена покупателя', color:'#f59e0b', yAxis:'price', fmt:v=>Math.round(v)+' ₽' },
  { key:'stock_wb',     label:'Остатки WB',      color:'#60a5fa', yAxis:'qty',   fmt:v=>Math.round(v)+' шт.' },
  { key:'drr_buyout',   label:'ДРР продаж',      color:'#a855f7', yAxis:'pct',   fmt:v=>Number(v).toFixed(1)+'%' },
];

const PERIODS = [
  { label:'7 дн',  days:7,  mode:'day' },
  { label:'14 дн', days:14, mode:'day' },
  { label:'30 дн', days:30, mode:'day' },
  { label:'2 мес', days:60, mode:'week' },
  { label:'3 мес', days:90, mode:'week' },
];

function aggregateByWeek(data) {
  const weeks = {};
  data.forEach(d => {
    const dt = new Date(d.date+'T00:00:00');
    const day = dt.getDay();
    const diff = day===0 ? -6 : 1-day;
    const mon = new Date(dt); mon.setDate(dt.getDate()+diff);
    const key = toISO(mon);
    if (!weeks[key]) weeks[key] = { date:key, orders_sum:0, spend_all:0, cp_sum:0, cp_cnt:0, stock_wb:null, drr_sum:0, drr_cnt:0 };
    weeks[key].orders_sum += Number(d.orders_sum||0);
    weeks[key].spend_all  += Number(d.spend_all||0);
    if (d.client_price) { weeks[key].cp_sum+=Number(d.client_price); weeks[key].cp_cnt++; }
    if (d.stock_wb!=null) weeks[key].stock_wb=Number(d.stock_wb);
    if (d.drr_buyout!=null) { weeks[key].drr_sum+=Number(d.drr_buyout); weeks[key].drr_cnt++; }
  });
  return Object.values(weeks).sort((a,b)=>a.date.localeCompare(b.date)).map(w=>({
    date:w.date,
    orders_sum:   Math.round(w.orders_sum),
    spend_all:    Math.round(w.spend_all),
    client_price: w.cp_cnt>0 ? Math.round(w.cp_sum/w.cp_cnt) : null,
    stock_wb:     w.stock_wb,
    drr_buyout:   w.drr_cnt>0 ? Math.round(w.drr_sum/w.drr_cnt*10)/10 : null,
  }));
}

const CustomTooltip = ({ active, payload, label, realData }) => {
  if (!active||!payload?.length) return null;
  const real = realData?.find(d=>d.date===label) || {};
  return (
    <div style={{ background:'#0d1420', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'8px 12px', fontSize:11 }}>
      <div style={{ color:'rgba(200,215,235,0.5)', marginBottom:4 }}>{fmtDate(label)}</div>
      {payload.map(p => {
        const m = METRICS.find(x=>x.key===p.dataKey);
        const realVal = real[m?.key];
        return <div key={p.dataKey} style={{ color:p.color, marginBottom:2 }}>{p.name}: <b>{realVal!=null ? m?.fmt(realVal) : '—'}</b></div>;
      })}
    </div>
  );
};

// Нормализуем данные: каждая метрика 0-100% от своего максимума
function normalizeData(data) {
  const maxMap = {};
  METRICS.forEach(m => {
    const vals = data.map(d=>d[m.key]).filter(v=>v!=null&&v>0);
    maxMap[m.key] = vals.length ? Math.max(...vals) : 1;
  });
  return data.map(d => {
    const norm = { date: d.date };
    METRICS.forEach(m => { norm[m.key] = d[m.key]!=null ? Math.round(d[m.key]/maxMap[m.key]*100*10)/10 : null; });
    return norm;
  });
}

export default function AdvChart({ nmId }) {
  const [period, setPeriod] = useState(PERIODS[1]);
  const [visible, setVisible] = useState({ orders_sum:true, spend_all:true, client_price:true, stock_wb:true, drr_buyout:true });
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const to = toISO(new Date());
    const from = toISO(new Date(Date.now()-90*86400000));
    setLoading(true);
    api.get(`/advertising/chart/${nmId}?from=${from}&to=${to}`)
      .then(r => setRawData(r.data.days||[]))
      .catch(()=>setRawData([]))
      .finally(()=>setLoading(false));
  }, [nmId]);

  const cutFrom = toISO(new Date(Date.now()-period.days*86400000));
  const filtered = rawData.filter(d=>d.date>=cutFrom);
  const chartData = period.mode==='week' ? aggregateByWeek(filtered) : filtered;
  const normData = normalizeData(chartData);

  if (loading) return <div style={{ padding:'16px 0', color:'rgba(200,215,235,0.35)', fontSize:12 }}>Загружаем...</div>;

  return (
    <div style={{ paddingTop:10 }}>
      <div style={{ display:'flex', gap:5, marginBottom:10, flexWrap:'wrap' }}>
        {PERIODS.map(p=>(
          <button key={p.label} onClick={()=>setPeriod(p)} style={{
            padding:'2px 8px', fontSize:10, borderRadius:5, cursor:'pointer',
            background: period===p?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.04)',
            border: period===p?'1px solid rgba(59,130,246,0.4)':'1px solid rgba(255,255,255,0.08)',
            color: period===p?'#60a5fa':'rgba(200,215,235,0.5)',
          }}>{p.label}{p.mode==='week'?' (нед.)':' (дн.)'}</button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={normData} margin={{ top:4, right:8, left:0, bottom:0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
          <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize:9, fill:'rgba(200,215,235,0.35)' }} tickLine={false} axisLine={false}/>
          <YAxis domain={[0,100]} tick={{ fontSize:9, fill:'rgba(200,215,235,0.2)' }} tickLine={false} axisLine={false} width={28} tickFormatter={v=>v===100?'макс':v===0?'0':v+'%'} ticks={[0,25,50,75,100]}/>
          <Tooltip content={<CustomTooltip realData={chartData}/>}/>
          {METRICS.map(m=>visible[m.key]&&(
            <Line key={m.key} type="monotone" dataKey={m.key} name={m.label}
              stroke={m.color} strokeWidth={2} dot={false} activeDot={{ r:3 }} connectNulls/>
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8, justifyContent:'center' }}>
        {METRICS.map(m=>{
          const last = [...chartData].reverse().find(d=>d[m.key]!=null&&d[m.key]>0);
          const lastVal = last ? m.fmt(last[m.key]) : null;
          return (
          <button key={m.key} onClick={()=>setVisible(v=>({...v,[m.key]:!v[m.key]}))} style={{
            display:'flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:20, cursor:'pointer', fontSize:10,
            background: visible[m.key]?`${m.color}18`:'rgba(255,255,255,0.03)',
            border: `1px solid ${visible[m.key]?m.color+'55':'rgba(255,255,255,0.07)'}`,
            color: visible[m.key]?m.color:'rgba(200,215,235,0.3)',
          }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:visible[m.key]?m.color:'rgba(255,255,255,0.15)', flexShrink:0 }}/>
            {m.label}
            {lastVal && <span style={{ opacity:0.7, marginLeft:2 }}>· {lastVal}</span>}
          </button>
          );
        })}
      </div>
    </div>
  );
}
